import React, { useCallback, useState } from 'react';
import { PiPath, PiCopy } from 'react-icons/pi';
import { useMapContextMenu, VesselFeature } from './MapContextMenuContext';
import { MenuItem } from './MenuItem';
import { useFetchVectorTrack } from '@ais-ui/api';
import {
  useLayers,
  useOpenLayersContext,
  trackPathToVectorLayer,
  initLayerItem,
  baseColours,
} from '@ais-ui/react-openlayers';
import { formatCoordinateForCopy, formatVesselName } from '@ais-ui/utils';
import { useToast } from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { useSettings } from '../../Settings/SettingsProvider';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { isStationaryTrack } from './isStationaryTrack';

interface VesselContextMenuItemsProps {
  vessel: VesselFeature['data'];
}

const TRACK_HOURS = 12;

export const VesselContextMenuItems = ({
  vessel,
}: VesselContextMenuItemsProps) => {
  const { closeContextMenu } = useMapContextMenu();
  const { settings } = useSettings();
  const fetchVectorTrack = useFetchVectorTrack();
  const { addExistingLayerItem, doesLayerExist } = useLayers();
  const { displayProjection, dataProjection } = useOpenLayersContext();
  const { formatTimeWithTimeZone } = useDateTimeFormat();
  const { toast } = useToast();
  const { messages } = useLocalization();
  const [isLoading, setIsLoading] = useState(false);

  const trackLayerId = `vessel-track-${vessel.mmsi}`;
  const trackExists = doesLayerExist(trackLayerId);

  const handleShowTrack = useCallback(async () => {
    if (trackExists) {
      closeContextMenu();
      return;
    }

    const label = formatVesselName(vessel.name) || vessel.mmsi;

    setIsLoading(true);
    try {
      const endTime = new Date();
      const startTime = new Date(
        endTime.getTime() - TRACK_HOURS * 60 * 60 * 1000,
      );

      const result = await fetchVectorTrack({
        mmsi: Number(vessel.mmsi),
        startTime,
        endTime,
      });

      const track = result.tracks[0];
      if (!track?.path) {
        toast({
          title: messages.genericToastTitleWarning,
          description: `No track data available for ${label} in the last ${TRACK_HOURS} hours.`,
          variant: 'warning',
        });
        return;
      }

      // Pick a colour based on MMSI hash for consistency
      const colourKeys = Object.keys(baseColours) as Array<
        keyof typeof baseColours
      >;
      const colourIndex = Math.abs(Number(vessel.mmsi)) % colourKeys.length;
      const color = baseColours[colourKeys[colourIndex]];

      const vectorLayer = trackPathToVectorLayer(
        track,
        displayProjection,
        dataProjection,
        { color, label, colorBy: 'sog' },
      );

      const layerItem = initLayerItem(
        vectorLayer,
        label,
        false,
        true,
        true,
        false,
        {
          type: 'vector-track',
          track,
          defaultColor: color,
          defaultColorMode: 'sog',
          colorBy: 'sog',
          layerMetadata: vessel.mmsi,
          trackHours: TRACK_HOURS,
          lastUpdatedAt: new Date().toISOString(),
        },
        trackLayerId,
      );
      layerItem.color = color;
      layerItem.metadata = `${TRACK_HOURS}h • ${formatTimeWithTimeZone(new Date(), true)}`;

      addExistingLayerItem(vectorLayer, layerItem, 'above-ships');

      if (isStationaryTrack(track)) {
        toast({
          title: messages.genericToastTitleInfo,
          description: `${label} appears stationary — all reported speeds are 0 kn over the last ${TRACK_HOURS} hours.`,
          variant: 'info',
        });
      }
    } catch {
      toast({
        title: messages.genericToastTitleWarning,
        description: `No track data available for ${label} in the last ${TRACK_HOURS} hours.`,
        variant: 'warning',
      });
    } finally {
      setIsLoading(false);
      closeContextMenu();
    }
  }, [
    vessel,
    trackLayerId,
    trackExists,
    fetchVectorTrack,
    displayProjection,
    dataProjection,
    addExistingLayerItem,
    closeContextMenu,
    formatTimeWithTimeZone,
    toast,
    messages,
  ]);

  const handleCopyCoordinate = useCallback(async () => {
    const format = settings['ribbonPlugin.settings']?.cursor_position_format;
    const formatted = formatCoordinateForCopy(
      vessel.longitude,
      vessel.latitude,
      format,
    );
    if (formatted) {
      await navigator.clipboard.writeText(formatted);
    }
    closeContextMenu();
  }, [vessel, settings, closeContextMenu]);

  return (
    <>
      <MenuItem disabled={isLoading || trackExists} onClick={handleShowTrack}>
        <PiPath className="h-4 w-4" />
        {trackExists ? 'Track already shown' : 'Show Vector Track'}
      </MenuItem>

      <div className="-mx-1 my-1 h-px bg-border" />

      <MenuItem onClick={handleCopyCoordinate}>
        <PiCopy className="h-4 w-4" />
        Copy Coordinate
      </MenuItem>
    </>
  );
};
